import { useCustomMutation } from '@refinedev/core';
import { Modal, Input, InputNumber, Select, Radio, message } from 'antd';
import { useState, type FC } from 'react';

interface CouponCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CouponForm {
  name: string;
  discountType: 'percent' | 'amount';
  percentOff: number;
  amountOff: number;
  currency: string;
  duration: string;
  durationInMonths: number;
  maxRedemptions: number | null;
}

const INITIAL_FORM: CouponForm = {
  name: '',
  discountType: 'percent',
  percentOff: 10,
  amountOff: 500,
  currency: 'eur',
  duration: 'once',
  durationInMonths: 3,
  maxRedemptions: null,
};

const CouponCreateModal: FC<CouponCreateModalProps> = ({ open, onClose, onSuccess }) => {
  const { mutate, isLoading } = useCustomMutation();
  const [form, setForm] = useState<CouponForm>(INITIAL_FORM);

  const handleSubmit = () => {
    if (!form.name.trim()) {
      void message.warning('Le nom est requis');
      return;
    }

    const values: Record<string, unknown> = {
      name: form.name,
      duration: form.duration,
    };

    if (form.discountType === 'percent') {
      values.percentOff = form.percentOff;
    } else {
      values.amountOff = form.amountOff;
      values.currency = form.currency;
    }

    if (form.duration === 'repeating') {
      values.durationInMonths = form.durationInMonths;
    }

    if (form.maxRedemptions && form.maxRedemptions > 0) {
      values.maxRedemptions = form.maxRedemptions;
    }

    mutate(
      { url: '/api/v1/admin/billing/coupons', method: 'post', values },
      {
        onSuccess: () => {
          void message.success('Coupon cree');
          setForm(INITIAL_FORM);
          onSuccess();
        },
        onError: () => {
          void message.error('Erreur lors de la creation');
        },
      },
    );
  };

  return (
    <Modal
      title="Creer un coupon"
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={isLoading}
      okText="Creer"
      cancelText="Annuler"
      destroyOnClose
    >
      <div className="space-y-4 pt-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Nom
          </label>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Ex: Lancement Beta -20%"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Type de reduction
          </label>
          <Radio.Group
            value={form.discountType}
            onChange={(e) => setForm((f) => ({ ...f, discountType: e.target.value }))}
          >
            <Radio value="percent">Pourcentage</Radio>
            <Radio value="amount">Montant fixe</Radio>
          </Radio.Group>
        </div>

        {form.discountType === 'percent' ? (
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
              Pourcentage de reduction
            </label>
            <InputNumber
              className="w-full"
              min={1}
              max={100}
              value={form.percentOff}
              onChange={(v) => setForm((f) => ({ ...f, percentOff: v ?? 10 }))}
              addonAfter="%"
            />
          </div>
        ) : (
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                Montant (centimes)
              </label>
              <InputNumber
                className="w-full"
                min={1}
                value={form.amountOff}
                onChange={(v) => setForm((f) => ({ ...f, amountOff: v ?? 500 }))}
              />
            </div>
            <div className="w-28">
              <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                Devise
              </label>
              <Select
                className="w-full"
                value={form.currency}
                onChange={(v) => setForm((f) => ({ ...f, currency: v }))}
                options={[
                  { value: 'eur', label: 'EUR' },
                  { value: 'usd', label: 'USD' },
                  { value: 'gbp', label: 'GBP' },
                ]}
              />
            </div>
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Duree
          </label>
          <Select
            className="w-full"
            value={form.duration}
            onChange={(v) => setForm((f) => ({ ...f, duration: v }))}
            options={[
              { value: 'once', label: 'Une fois' },
              { value: 'repeating', label: 'Recurrent' },
              { value: 'forever', label: 'Permanent' },
            ]}
          />
        </div>

        {form.duration === 'repeating' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
              Nombre de mois
            </label>
            <InputNumber
              className="w-full"
              min={1}
              max={36}
              value={form.durationInMonths}
              onChange={(v) => setForm((f) => ({ ...f, durationInMonths: v ?? 3 }))}
            />
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Utilisations max (vide = illimite)
          </label>
          <InputNumber
            className="w-full"
            min={1}
            value={form.maxRedemptions}
            onChange={(v) => setForm((f) => ({ ...f, maxRedemptions: v }))}
            placeholder="Illimite"
          />
        </div>
      </div>
    </Modal>
  );
};

export default CouponCreateModal;
